import { focusFirstEmpty } from 'components/create-dialog/focusFirstEmptyRequired';
import {
    createContext,
    type ReactNode,
    type RefObject,
    useCallback,
    useContext,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
// `flushSync` commits the rules without `required` before `handleSubmit` reads them: the inputs set their rules as
// they render, and a deferred render would leave validation on the old ones and refuse the draft.
import { flushSync } from 'react-dom';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

/** A field as a draft save sees it, and the element that draws it. */
type DraftField = { label: string; required: boolean; isEmpty: boolean; element?: HTMLElement | null };

type DraftSaveModeValue = {
    /** True for the length of a Save as draft. */
    on: boolean;
    /** Records a field under the id of the wrapper that draws it; `null` forgets it. */
    report: (id: string, field: DraftField | null) => void;
};

const DraftSaveModeContext = createContext<DraftSaveModeValue | null>(null);

/** Around the fields of a form that can be saved as a draft, with the `mode` its `useDraftSubmit` returns. */
export function DraftSaveMode({ mode, children }: { mode: DraftSaveModeValue; children: ReactNode }) {
    return <DraftSaveModeContext.Provider value={mode}>{children}</DraftSaveModeContext.Provider>;
}

/**
 * True while the form around is saved as a draft. A field's required rule, and any check of its own that a value
 * is there, is off then; every other rule stands, so a filled value that is wrong still refuses the save.
 */
export function useDraftSaveMode(): boolean {
    return useContext(DraftSaveModeContext)?.on ?? false;
}

/**
 * Tells a draft save that the field drawn here, in `ref`, is required and whether it is empty, so the save can name
 * what is left to fill. A field without a label, or that does not say whether it is empty, is not counted.
 */
export function useDraftField(
    label: string | undefined,
    required: boolean,
    isEmpty: boolean | undefined,
    ref?: RefObject<HTMLElement | null>
): void {
    const report = useContext(DraftSaveModeContext)?.report;
    const id = useId();
    useLayoutEffect(() => {
        report?.(id, label && isEmpty !== undefined ? { label, required, isEmpty, element: ref?.current } : null);
    }, [report, id, label, required, isEmpty, ref]);
    useLayoutEffect(() => () => report?.(id, null), [report, id]);
}

/**
 * Saves a form as a draft: `handleSubmit` runs with the required rules off, and `onValid` gets the values and the
 * labels of the required fields left empty, in the order the form draws them. Render the form's fields inside
 * `<DraftSaveMode mode={mode}>`, which is how they learn of it. With `focusMissing`, the first of those fields on
 * screen takes the focus once the form has drawn its fields.
 */
export function useDraftSubmit<T extends FieldValues>(
    methods: Pick<UseFormReturn<T>, 'handleSubmit'>,
    onValid: (values: T, missingFields: string[]) => unknown,
    focusMissing = false
) {
    const [on, setOn] = useState(false);
    const [fields] = useState(() => new Map<string, DraftField>());
    const focusPending = useRef(focusMissing);
    const report = useCallback(
        (id: string, field: DraftField | null) => {
            if (field) fields.set(id, field);
            else fields.delete(id);
            if (field && focusPending.current) {
                focusPending.current = false;
                // On a timer, so the rest of the fields, and the values the form sets once mounted, are in first.
                setTimeout(() => focusFirstMissing(fields));
            }
        },
        [fields]
    );
    const mode = useMemo(() => ({ on, report }), [on, report]);

    async function saveAsDraft(): Promise<void> {
        flushSync(() => setOn(true));
        try {
            await methods.handleSubmit((values) => onValid(values, missingLabels(fields)))();
        } finally {
            setOn(false);
        }
    }

    return { mode, saveAsDraft };
}

function focusFirstMissing(fields: Map<string, DraftField>): void {
    const missing = [...fields.values()].flatMap((field) =>
        field.required && field.isEmpty && field.element ? [field.element] : []
    );
    missing.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    focusFirstEmpty(missing);
}

function missingLabels(fields: Map<string, DraftField>): string[] {
    const labels = [...fields.values()]
        .filter((field) => field.required && field.isEmpty)
        .map((field) => fieldName(field.label));
    return [...new Set(labels)];
}

/** A field named in a sentence: its label without the trailing colon some labels carry ("Concluded by:"). */
export function fieldName(label: string): string {
    return label.replace(/:\s*$/, '');
}
